import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { _className, EditableDBItemV3, EventType_Update, LL_H_C, LL_V_L, ModuleFE_Dialog, performAction, Show, stopPropagation, TS_Checkbox, TS_Dialog, TS_PropRenderer, } from '@nu-art/thunderstorm-frontend/index';
import { ModuleFE_DiseaseProfile, ModuleFE_DiseaseView, ModuleFE_MultiVar, } from '../../../_entity.js';
import { compare, dbObjectToId, exists, flatArray, MUSTNeverHappenException } from '@nu-art/ts-common';
import { Component_TagEditor, ConfigKeyFE_ComplaintsTagId, ConfigKeyFE_DiseasesViewsTagId, ConfigKeyFE_SourceTagId, Dialog_VariableEditor, DropDown_Tag, DropDown_Variable, ModuleFE_Tag, ModuleFE_Variable, QScrollWrapper, VariableWrapper, } from '@app/hcs-frontend';
import { ICONS } from '@app/styles';
import './Dialog_MultiVar_Editor_V2.scss';
import { variableType_Attribute } from '@app/hcs-shared';
export class Dialog_MultiVar_Editor_V2 extends TS_Dialog {
    //######################### Static #########################
    static defaultProps = {
        dialogId: 'dialog__mv-editor'
    };
    static show = (props) => {
        ModuleFE_Dialog.show({ content: _jsx(Dialog_MultiVar_Editor_V2, { ...props }), closeOverlayOnClick: () => false });
    };
    //######################### Life Cycle #########################
    __onVariableUpdated = (...params) => {
        if (params[0] !== EventType_Update)
            return;
        //Find if updated variable matches a value
        const columnIndex = this.state.multiVar.item.variables?.findIndex(column => column.variableId === params[1]._id);
        if (!exists(columnIndex))
            return;
        //Assume column data from the updated variable
        const columns = this.state.multiVar.editProp('variables', []);
        const column = columns.editProp(columnIndex, {});
        return column.updateObj({
            variableId: params[1]._id, //Variable id should be the same anyway
            tagIds: params[1].tagIds, //Overwrite tagIds
        });
    };
    deriveStateFromProps(nextProps, state) {
        state.diseaseProfile ??= this.getEditableDP(nextProps.diseaseProfileId);
        state.multiVar ??= this.getEditableMV(state.diseaseProfile, nextProps.multiVar);
        if (!state.selectedColumnIndex && state.multiVar.item.variables?.length)
            state.selectedColumnIndex = 0;
        //Make sure the first column in the MV holds tag group for disease views
        const dvTagId = ConfigKeyFE_DiseasesViewsTagId.get();
        const firstColumn = state.multiVar.item.variables[0];
        if (!firstColumn.tagIds)
            firstColumn.tagIds = [];
        if (!firstColumn.tagIds.find(group => group.key === dvTagId))
            firstColumn.tagIds.push({ key: dvTagId, value: [] });
        return state;
    }
    //######################### Logic #########################
    getEditableMV(dp, mv) {
        return new EditableDBItemV3(mv ?? {
            diseaseProfileId: dp.item._id,
            referenceIds: [],
            variables: [{}]
        }, ModuleFE_MultiVar)
            .setOnChanged(async () => this.forceUpdate());
    }
    getEditableDP(dpId) {
        const dp = ModuleFE_DiseaseProfile.cache.unique(dpId);
        if (!dp)
            throw new MUSTNeverHappenException(`Could not find DP for given id ${dpId}`);
        return new EditableDBItemV3(dp, ModuleFE_DiseaseProfile)
            .setOnChanged(async () => this.forceUpdate());
    }
    getTagDataForColumn = (column, index) => {
        const variable = ModuleFE_Variable.cache.unique(column.item.variableId);
        if (!variable) {
            if (column.item.variableId)
                throw new MUSTNeverHappenException(`Could not get variable for id ${column.item.variableId}`);
            return;
        }
        const parentTagIds = variable.tagIds;
        if (!parentTagIds)
            return;
        //Gather tags, Filter out disease views group
        const dvTagId = ConfigKeyFE_DiseasesViewsTagId.get();
        let tags = VariableWrapper.dbTagsToUI(column.item.tagIds ?? []);
        tags = tags.filter(group => group.key._id !== dvTagId);
        //Gather scope of allowed groups
        const allowedGroupTags = parentTagIds.map(group => group.key);
        //Gather scope of allowed tags
        const allowedTags = flatArray(parentTagIds.map(group => group.value));
        //For any column that's not the first one, can return here
        if (index !== 0)
            return {
                tags,
                allowedTags,
                allowedGroupTags
            };
        //Add variants into the allowed groups and tags if the DP holds variants
        const dp = this.state.diseaseProfile.item;
        if (dp?.variantsGroupId) {
            const variantTag = ModuleFE_Tag.cache.unique(dp.variantsGroupId);
            if (!variantTag)
                throw new MUSTNeverHappenException(`Variant group with id ${dp.variantsGroupId} does not exist`);
            const tags = ModuleFE_Tag.cache.filter(i => i.tagIds.includes(variantTag._id));
            allowedGroupTags.push(variantTag._id);
            allowedTags.push(...tags.map(dbObjectToId));
        }
        return {
            tags,
            allowedTags,
            allowedGroupTags,
        };
    };
    getMatchingViews = () => {
        const mv = this.state.multiVar.item;
        if (!mv?.variables?.[0])
            return [];
        return ModuleFE_DiseaseView.cache.filter(dv => ModuleFE_DiseaseView.canVariableBeAddedToView(dv, mv));
    };
    save = async () => {
        this.setState({ dialogIsBusy: true }, () => {
            performAction(async () => {
                try {
                    await ModuleFE_MultiVar._v1.saveMultiVar({ multiVar: this.state.multiVar.item }).executeSync();
                    const currentDP = this.state.diseaseProfile.item;
                    const originalDP = ModuleFE_DiseaseProfile.cache.unique(currentDP._id);
                    //Save DP if it had changes
                    if (!compare(originalDP, currentDP))
                        await this.state.diseaseProfile.save();
                    this.closeDialog();
                }
                catch (err) {
                    this.logError(err);
                }
                finally {
                    if (this.mounted)
                        this.setState({ dialogIsBusy: false });
                }
            }, { type: 'notification', notificationLabels: 'Saving MultiVar' });
        });
    };
    delete = async () => {
        const mvId = this.state.multiVar.item._id;
        if (!mvId)
            return;
        this.setState({ dialogIsBusy: true }, () => {
            performAction(async () => {
                try {
                    await ModuleFE_MultiVar._v1.deleteMultiVar({ multiVarId: mvId }).executeSync();
                    this.closeDialog();
                }
                catch (err) {
                    this.logError(err);
                }
                finally {
                    if (this.mounted)
                        this.setState({ dialogIsBusy: false });
                }
            }, { type: 'notification', notificationLabels: 'Deleting MultiVar' });
        });
    };
    onTagsChanged = (column, index, uiTags) => {
        const groups = uiTags.map(group => ({ key: group.key._id, value: group.value.map(dbObjectToId) }));
        //Any column that isn't the first one can return here
        if (index !== 0)
            return column.updateObj({ tagIds: groups });
        //First column - add disease view group back in
        const dvTagId = ConfigKeyFE_DiseasesViewsTagId.get();
        const dvGroup = column.item.tagIds?.find(group => group.key === dvTagId);
        if (!dvGroup)
            throw new MUSTNeverHappenException('First column did not contain a tag group for disease views');
        groups.push(dvGroup);
        return column.updateObj({ tagIds: groups });
    };
    //######################### Render #########################
    renderHeader = () => {
        const id = this.state.multiVar.item._id;
        return _jsxs(Show, { children: [_jsx(Show.If, { condition: !id, children: "Create MultiVar" }), _jsx(Show.If, { condition: !!id, children: "Edit MultiVar" }), _jsx(Show.If, { condition: true, children: _jsx(ICONS.x.component, { onClick: () => this.closeDialog() }) })] });
    };
    renderBody = () => {
        return _jsxs(_Fragment, { children: [this.renderGeneral_IsChiefComplaint(), this.renderColumnEditor()] });
    };
    buttons = () => {
        const buttons = {
            left: [],
            right: [
                {
                    content: 'Cancel',
                    onClick: () => this.closeDialog(),
                    disabled: this.state.dialogIsBusy
                },
                {
                    content: 'Save',
                    onClick: this.save,
                    disabled: this.state.dialogIsBusy
                }
            ]
        };
        if (this.state.multiVar.item._id)
            buttons.left?.push({
                content: 'Delete',
                onClick: this.delete,
                disabled: this.state.dialogIsBusy
            });
        return buttons;
    };
    //######################### Render - MV General #########################
    renderGeneral_IsChiefComplaint = () => {
        const complaintTagId = ConfigKeyFE_ComplaintsTagId.get();
        const mainVariable = ModuleFE_Variable.cache.unique(this.state.multiVar.item.variables?.[0]?.variableId);
        if (!mainVariable || !mainVariable._tagIds.includes(complaintTagId))
            return;
        const chiefComplaintIds = this.state.diseaseProfile.editProp('chiefComplaintIds', []);
        const isChiefComplaint = chiefComplaintIds.item.includes(mainVariable._id);
        return _jsx(TS_Checkbox, { checked: isChiefComplaint, onCheck: () => {
                //Add to chief complaints
                if (!isChiefComplaint)
                    return chiefComplaintIds.updateArrayAt(mainVariable._id);
                const index = chiefComplaintIds.item.indexOf(mainVariable._id);
                return chiefComplaintIds.removeArrayItem(index);
            }, children: "Is Chief Complaint" });
    };
    //######################### Render - Column Editor #########################
    renderColumnEditor = () => {
        return _jsx(TS_PropRenderer.Vertical, { label: 'Column Editor', className: 'column-editor__wrapper', children: _jsxs(LL_H_C, { className: 'column-editor', children: [this.renderColumnEditor_List(), this.renderColumnEditor_Editor()] }) });
    };
    renderColumnEditor_List = () => {
        const columns = this.state.multiVar.editProp('variables', []);
        return _jsxs(LL_V_L, { className: 'column-editor__list', children: [_jsx(QScrollWrapper, { children: columns.item.map((column, index) => {
                        if (!column)
                            return;
                        const variable = ModuleFE_Variable.cache.unique(column.variableId);
                        if (column.variableId && !variable)
                            throw new MUSTNeverHappenException(`Could not find variable for id ${column.variableId}`);
                        const className = _className('column-editor__list__column', index === this.state.selectedColumnIndex && 'selected', index === 0 && 'main-column');
                        return _jsx(LL_H_C, { className: className, onClick: () => {
                                this.setState({ selectedColumnIndex: index });
                            }, children: _jsxs(Show, { children: [_jsx(Show.If, { condition: true, children: _jsx("span", { children: variable?.name ?? 'New Column' }) }), _jsx(Show.If, { condition: index !== 0, children: _jsx(ICONS.bin.component, { onClick: e => {
                                                stopPropagation(e);
                                                if (index !== this.state.selectedColumnIndex)
                                                    return columns.removeArrayItem(index);
                                                this.setState({ selectedColumnIndex: 0 }, () => columns.removeArrayItem(index));
                                            } }) })] }) }, index);
                    }) }), _jsx("div", { className: 'column-editor__list__add', onClick: async () => {
                        await columns.updateArrayAt({});
                        this.setState({ selectedColumnIndex: columns.item.length - 1 });
                    }, children: "Add New Column" })] });
    };
    renderColumnEditor_Editor = () => {
        const index = this.state.selectedColumnIndex;
        if (!exists(index))
            return;
        const columns = this.state.multiVar.editProp('variables', []);
        const column = columns.editProp(index, {});
        return _jsxs(QScrollWrapper, { className: 'column-editor__editor', children: [this.renderColumnEditor_Editor_Variable(column, index), this.renderColumnEditor_Editor_DiseaseView(column, index), this.renderColumnEditor_Editor_Tags(column, index)] });
    };
    renderColumnEditor_Editor_Variable = (column, index) => {
        const variable = ModuleFE_Variable.cache.unique(column.item.variableId);
        if (column.item.variableId && !variable)
            throw new MUSTNeverHappenException(`Could not get variable for id ${column.item.variableId}`);
        //Column variable should only be editable if the column is new
        const originalVariableIdForColumn = this.props.multiVar?.variables?.[index]?.variableId;
        const editable = !variable || originalVariableIdForColumn !== variable?._id;
        const existingVarIds = this.state.multiVar.item.variables?.map(column => column.variableId) ?? [];
        return _jsx(TS_PropRenderer.Vertical, { label: 'Variable', children: _jsx(LL_H_C, { className: 'column-editor__editor__variable-selector', children: _jsxs(Show, { children: [_jsx(Show.If, { condition: editable, children: _jsx(DropDown_Variable, { selected: column.item.variableId, queryFilter: variable => variable.type !== variableType_Attribute
                                    && !existingVarIds.includes(variable._id), onSelected: variable => {
                                    const dvTagId = ConfigKeyFE_DiseasesViewsTagId.get();
                                    const groups = variable.tagIds;
                                    if (!groups.find(group => group.key === dvTagId))
                                        groups.push({ key: dvTagId, value: [] });
                                    return column.updateObj({
                                        variableId: variable._id,
                                        tagIds: groups,
                                    });
                                } }) }), _jsx(Show.If, { condition: !editable, children: _jsx("div", { className: 'column-editor__editor__data-node', children: variable?.name }) }), _jsx(Show.If, { condition: !!column.item.variableId, children: _jsx(ICONS.edit.component, { className: 'variable-edit', onClick: () => {
                                    const variable = ModuleFE_Variable.cache.unique(column.item.variableId);
                                    if (!variable)
                                        throw new MUSTNeverHappenException(`Could not get variable for id ${column.item.variableId}`);
                                    const tagId = variable.tagIds.find(group => group.key === ConfigKeyFE_SourceTagId.get())?.value[0];
                                    Dialog_VariableEditor.show(tagId, variable);
                                } }) })] }) }) });
    };
    renderColumnEditor_Editor_DiseaseView = (column, index) => {
        if (index !== 0)
            return;
        const matchingViews = this.getMatchingViews();
        const allViews = ModuleFE_DiseaseView.cache.all();
        const tags = column.editProp('tagIds', []);
        const groupIndex = tags.item.findIndex(group => group?.key === ConfigKeyFE_DiseasesViewsTagId.get());
        if (groupIndex === -1)
            throw new MUSTNeverHappenException('first column in MV must have a tag group for disease views!');
        const group = tags.editProp(groupIndex, {});
        return _jsx(TS_PropRenderer.Horizontal, { label: `Disease View (${matchingViews.length} Matching)`, children: _jsx(DropDown_Tag.selectable, { className: 'disease-view-selector', selected: group.item?.value?.[0], onSelected: tag => {
                    return group.updateObj({ value: [tag._id] });
                }, placeholder: 'Select Disease View', queryFilter: tag => {
                    return allViews.some(view => view.enforcedTags.includes(tag._id));
                } }) });
    };
    renderColumnEditor_Editor_Tags = (column, index) => {
        const tagData = this.getTagDataForColumn(column, index);
        if (!tagData)
            return;
        const { tags, allowedTags, allowedGroupTags } = tagData;
        return _jsx(TS_PropRenderer.Vertical, { label: 'Tags', children: _jsx(Component_TagEditor, { tags: tags, singleTagFilter: (tag, groupTag) => tag.tagIds[0] === groupTag._id && (allowedTags.includes(tag._id)), groupTagFilter: (groupTag) => allowedGroupTags.includes(groupTag._id), onChange: () => this.onTagsChanged(column, index, tags) }, index) });
    };
}
//# sourceMappingURL=Dialog_MultiVar_Editor_V2.js.map